import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";


export const SUPPORT_CATEGORIES = [
  { value: "boost", label: "Boost / Kalender funktioniert nicht" },
  { value: "billing", label: "Coins, Zahlung, Rechnung" },
  { value: "landing", label: "Salon-Seite / Bilder" },
  { value: "ads", label: "Google-Anzeige" },
  { value: "other", label: "Sonstiges / Feedback" },
] as const;

const schema = z.object({
  category: z.enum(["boost", "billing", "landing", "ads", "other"]),
  subject: z.string().trim().min(3).max(120),
  message: z.string().trim().min(10).max(4000),
  email: z.string().trim().email().max(255),
  name: z.string().trim().max(120).optional(),
  /** Data-URL eines bereits komprimierten Screenshots (optional). */
  attachment: z.string().max(3_500_000).optional(),
  attachmentName: z.string().trim().max(160).optional(),
  context: z.record(z.string(), z.string()).optional(),
});

export const createSupportTicket = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => schema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Optionale Identität: eingeloggte Nutzer schicken einen Bearer-Token mit.
    let userId: string | null = null;
    let salonId: string | null = null;
    const auth = getRequestHeader("authorization");
    const token = auth?.startsWith("Bearer ") ? auth.slice(7) : null;
    if (token) {
      const { data: userData } = await supabaseAdmin.auth.getUser(token);
      userId = userData.user?.id ?? null;
      if (userId) {
        const { data: salon } = await supabaseAdmin
          .from("salons")
          .select("id")
          .eq("owner_id", userId)
          .maybeSingle();
        salonId = salon?.id ?? null;
      }
    }

    // Einfaches Rate-Limit: max. 3 Tickets pro E-Mail in 10 Minuten.
    const since = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const { count } = await supabaseAdmin
      .from("support_tickets")
      .select("id", { count: "exact", head: true })
      .eq("email", data.email)
      .gte("created_at", since);
    if ((count ?? 0) >= 3) {
      return { ok: false as const, error: "rate_limited" as const };
    }

    const { data: ticket, error } = await supabaseAdmin
      .from("support_tickets")
      .insert({
        category: data.category,
        subject: data.subject,
        message: data.message,
        email: data.email,
        name: data.name ?? null,
        user_id: userId,
        salon_id: salonId,
        context: data.context ?? null,
      })
      .select("id, ticket_number")
      .single();

    if (error || !ticket) {
      return { ok: false as const, error: "failed" as const };
    }

    if (data.attachment) {
      const upload = await uploadAttachment(
        supabaseAdmin,
        ticket.id,
        data.attachment,
        data.attachmentName,
      );
      if (upload) {
        await supabaseAdmin
          .from("support_tickets")
          .update({ attachment_path: upload })
          .eq("id", ticket.id);
      }
    }

    await supabaseAdmin.from("system_logs").insert({
      kind: "support_ticket",
      level: "info",
      message: `Neues Support-Ticket ${ticket.ticket_number}: ${data.subject}`,
      context: { ticket_id: ticket.id, category: data.category, email: data.email },
    });

    return { ok: true as const, ticketNumber: ticket.ticket_number };
  });

type AdminClient = Awaited<
  typeof import("@/integrations/supabase/client.server")
>["supabaseAdmin"];

async function uploadAttachment(
  admin: AdminClient,
  ticketId: string,
  dataUrl: string,
  fileName?: string,
): Promise<string | null> {
  const match = /^data:(image\/[a-zA-Z+]+);base64,(.+)$/.exec(dataUrl);
  if (!match) return null;
  const [, mime, base64] = match;
  try {
    const bytes = Buffer.from(base64!, "base64");
    if (bytes.byteLength > 5 * 1024 * 1024) return null;
    const extension = mime!.split("/")[1]!.replace("+xml", "");
    const path = `${ticketId}/${(fileName ?? "screenshot").replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 60)}.${extension}`;
    const { error } = await admin.storage
      .from("support-attachments")
      .upload(path, bytes, { contentType: mime!, upsert: true });
    if (error) return null;
    return path;
  } catch {
    return null;
  }
}
